// Typed API client shared by the web app (and usable by tests). API §2.
import type { z } from 'zod';
import type {
  createFamilyResponse,
  currentCycleResponse,
  editionDetailResponse,
  editorCandidatesResponse,
  errorEnvelopeSchema,
  familyDetailResponse,
  homeResponse,
  inviteInfoResponse,
  inviteResponse,
  joinFamilyResponse,
  listEditionsResponse,
  listFamiliesResponse,
  listInvitesResponse,
  listMembersResponse,
  listNotificationsResponse,
  listOccasionsResponse,
  listPetsResponse,
  myContributionsResponse,
  notificationPreferencesResponse,
  presignResponse,
  pushConfigResponse,
  setThemeResponse,
  testPushResponse,
  themeSuggestionsResponse,
} from './api.js';
import type {
  contributionSchema,
  occasionSchema,
  petSchema,
  photoSchema,
  promptSchema,
  reactionSchema,
  userSchema,
} from './entities.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  /** API §6 — the client shows drafts-preserved re-login on this one. */
  get isAuthExpired() {
    return this.status === 401;
  }
  get isRateLimited() {
    return this.status === 429;
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Returns the current access token, or null when signed out. */
  getToken: () => string | Promise<string | null> | null;
  /** Called once on 401 so the caller can silently refresh; return true to retry. */
  onUnauthorized?: () => Promise<boolean>;
  fetchImpl?: typeof fetch;
}

type Infer<T extends z.ZodTypeAny> = z.infer<T>;

export function createApiClient(opts: ApiClientOptions) {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const base = opts.baseUrl.replace(/\/$/, '');

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    retry = true,
  ): Promise<T> {
    const token = await opts.getToken();
    const res = await fetchImpl(`${base}/v1${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'include',
    });

    if (res.status === 401 && retry && opts.onUnauthorized) {
      const refreshed = await opts.onUnauthorized();
      if (refreshed) return request<T>(method, path, body, false);
    }

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    const json = text ? JSON.parse(text) : {};
    if (!res.ok) {
      const env = json as Infer<typeof errorEnvelopeSchema>;
      throw new ApiError(
        res.status,
        env?.error?.code ?? 'unknown',
        env?.error?.message ?? 'Something went wrong',
        env?.error?.details,
      );
    }
    return json as T;
  }

  const qs = (params: Record<string, unknown>) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
    }
    const s = sp.toString();
    return s ? `?${s}` : '';
  };

  return {
    request,

    // Users
    me: () => request<Infer<typeof userSchema>>('GET', '/users/me'),
    updateMe: (body: unknown) => request<Infer<typeof userSchema>>('PUT', '/users/me', body),
    deleteMe: () => request<{ message: string }>('DELETE', '/users/me', { confirmation: 'DELETE' }),

    // Home
    home: () => request<Infer<typeof homeResponse>>('GET', '/home'),

    // Families
    listFamilies: () => request<Infer<typeof listFamiliesResponse>>('GET', '/families'),
    createFamily: (body: unknown) =>
      request<Infer<typeof createFamilyResponse>>('POST', '/families', body),
    family: (id: string) => request<Infer<typeof familyDetailResponse>>('GET', `/families/${id}`),
    updateFamily: (id: string, body: unknown) =>
      request<Infer<typeof familyDetailResponse>>('PUT', `/families/${id}`, body),
    members: (id: string) =>
      request<Infer<typeof listMembersResponse>>('GET', `/families/${id}/members`),
    createInvite: (id: string) =>
      request<Infer<typeof inviteResponse>>('POST', `/families/${id}/invite`, {}),
    listInvites: (id: string) =>
      request<Infer<typeof listInvitesResponse>>('GET', `/families/${id}/invites`),
    revokeInvite: (id: string, inviteId: string) =>
      request<{ message: string }>('DELETE', `/families/${id}/invites/${inviteId}`),
    inviteInfo: (token: string) =>
      request<Infer<typeof inviteInfoResponse>>('GET', `/invites/${token}`),
    join: (id: string, token: string) =>
      request<Infer<typeof joinFamilyResponse>>('POST', `/families/${id}/join`, {
        invite_token: token,
      }),
    joinByToken: (token: string) =>
      request<Infer<typeof joinFamilyResponse>>('POST', '/join', { invite_token: token }),
    removeMember: (id: string, uid: string) =>
      request<{ message: string }>('DELETE', `/families/${id}/members/${uid}`),
    setRole: (id: string, uid: string, role: 'admin' | 'member') =>
      request<{ message: string }>('PUT', `/families/${id}/members/${uid}/role`, { role }),
    grantArchive: (id: string, uid: string, access_from: string) =>
      request<{ message: string }>('POST', `/families/${id}/members/${uid}/grant-archive`, {
        access_from,
      }),

    // Pets
    pets: (id: string) => request<Infer<typeof listPetsResponse>>('GET', `/families/${id}/pets`),
    createPet: (id: string, body: unknown) =>
      request<Infer<typeof petSchema>>('POST', `/families/${id}/pets`, body),
    updatePet: (id: string, pid: string, body: unknown) =>
      request<Infer<typeof petSchema>>('PUT', `/families/${id}/pets/${pid}`, body),
    deletePet: (id: string, pid: string) =>
      request<{ message: string }>('DELETE', `/families/${id}/pets/${pid}`),

    // Cycles & prompts
    currentCycle: (familyId: string) =>
      request<Infer<typeof currentCycleResponse>>('GET', `/families/${familyId}/cycles/current`),
    themeSuggestions: (cycleId: string) =>
      request<Infer<typeof themeSuggestionsResponse>>(
        'GET',
        `/cycles/${cycleId}/theme-suggestions`,
      ),
    setTheme: (cycleId: string, theme: string) =>
      request<Infer<typeof setThemeResponse>>('POST', `/cycles/${cycleId}/theme`, { theme }),
    addPrompt: (cycleId: string, text: string) =>
      request<Infer<typeof promptSchema>>('POST', `/cycles/${cycleId}/prompts`, { text }),
    cycleStats: (cycleId: string) =>
      request<{ submitted_count: number; total_eligible_members: number }>(
        'GET',
        `/cycles/${cycleId}/contributions/stats`,
      ),

    // Contributions
    createContribution: (body: unknown) =>
      request<Infer<typeof contributionSchema>>('POST', '/contributions', body),
    updateContribution: (id: string, body: unknown) =>
      request<Infer<typeof contributionSchema>>('PUT', `/contributions/${id}`, body),
    deleteContribution: (id: string) =>
      request<{ message: string }>('DELETE', `/contributions/${id}`),
    myContributions: (params: Record<string, unknown> = {}) =>
      request<Infer<typeof myContributionsResponse>>('GET', `/contributions/mine${qs(params)}`),

    // Photos
    presignPhoto: (body: unknown) =>
      request<Infer<typeof presignResponse>>('POST', '/photos/presign', body),
    confirmPhoto: (body: unknown) =>
      request<Infer<typeof photoSchema>>('POST', '/photos/confirm', body),
    deletePhoto: (id: string) => request<{ message: string }>('DELETE', `/photos/${id}`),

    // Editions
    editions: (params: Record<string, unknown> = {}) =>
      request<Infer<typeof listEditionsResponse>>('GET', `/editions${qs(params)}`),
    edition: (id: string) => request<Infer<typeof editionDetailResponse>>('GET', `/editions/${id}`),
    markEditionOpened: (id: string) => request<{ message: string }>('POST', `/editions/${id}/open`),
    react: (id: string, body: unknown) =>
      request<{ reaction: Infer<typeof reactionSchema> }>(
        'POST',
        `/editions/${id}/reactions`,
        body,
      ),
    unreact: (id: string, rid: string) =>
      request<{ message: string }>('DELETE', `/editions/${id}/reactions/${rid}`),

    // Occasions
    occasions: (familyId: string, params: Record<string, unknown> = {}) =>
      request<Infer<typeof listOccasionsResponse>>(
        'GET',
        `/families/${familyId}/occasions${qs(params)}`,
      ),
    myOccasions: (params: Record<string, unknown> = {}) =>
      request<Infer<typeof listOccasionsResponse>>('GET', `/occasions${qs(params)}`),
    createOccasion: (familyId: string, body: unknown) =>
      request<Infer<typeof occasionSchema>>('POST', `/families/${familyId}/occasions`, body),
    updateOccasion: (oid: string, body: unknown) =>
      request<Infer<typeof occasionSchema>>('PUT', `/occasions/${oid}`, body),
    deleteOccasion: (oid: string) => request<{ message: string }>('DELETE', `/occasions/${oid}`),

    // Notifications
    notifications: (params: Record<string, unknown> = {}) =>
      request<Infer<typeof listNotificationsResponse>>('GET', `/notifications${qs(params)}`),
    markRead: (id: string) => request<{ message: string }>('PUT', `/notifications/${id}/read`),
    markAllRead: () =>
      request<{ message: string; count: number }>('PUT', '/notifications/read-all'),
    notificationPreferences: () =>
      request<Infer<typeof notificationPreferencesResponse>>('GET', '/notifications/preferences'),
    updateNotificationPreferences: (body: unknown) =>
      request<Infer<typeof notificationPreferencesResponse>>(
        'PUT',
        '/notifications/preferences',
        body,
      ),

    // Devices
    registerDevice: (body: unknown) => request<{ device_id: string }>('POST', '/devices', body),
    unregisterDevice: (endpoint: string) =>
      request<{ message: string }>('DELETE', `/devices${qs({ endpoint })}`),
    pushConfig: () => request<Infer<typeof pushConfigResponse>>('GET', '/devices/push-config'),
    testPush: () => request<Infer<typeof testPushResponse>>('POST', '/devices/test-push'),

    // Editor handoff + admin
    editorCandidates: (familyId: string) =>
      request<Infer<typeof editorCandidatesResponse>>(
        'GET',
        `/families/${familyId}/editor-candidates`,
      ),
    handoff: (familyId: string, next_editor_user_id: string) =>
      request<{ next_cycle: unknown }>('POST', `/families/${familyId}/editor-handoff`, {
        next_editor_user_id,
      }),
    emergencyEditor: (familyId: string, new_editor_user_id: string) =>
      request<{ message: string }>('POST', `/families/${familyId}/admin/emergency-editor`, {
        new_editor_user_id,
      }),
    forceClose: (familyId: string) =>
      request<{ message: string }>('POST', `/families/${familyId}/admin/force-close`, {}),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
